import type {IntlShape} from 'react-intl';
import {
    TimeTrackingImportColumnMapping,
    TimeTrackingImportDataMappingAction,
    TimeTrackingImportDataMappingDto,
    TimeTrackingImportDataMappingTarget,
    TimeTrackingImportEntityKind,
    TimeTrackingImportIssueDto,
    TimeTrackingImportPreviewDto,
    TimeTrackingImportPreviewRowDto,
    TimeTrackingImportRowStatus,
} from '../../../features/time-tracking-import/types';
import {Lx} from '../../../i18n/consts';
import {
    EMPTY_FILE_ROW_CHANGES,
    getImportEntityKindColumn,
    getImportOvertimeColumn,
    getManualImportRowValue,
    ImportFileRowChanges,
    ManualImportRow,
} from './manualRows';

export interface TimeTrackingImportDataMappingCandidate {
    field: string;
    sourceValue: string;
    sourceValues: string[];
    rowNumbers: number[];
    target: TimeTrackingImportDataMappingTarget;
    defaultAction: TimeTrackingImportDataMappingAction;
}

const NEW_ENTITY_WARNING_CODES = new Set([
    'new_employee_will_be_created',
    'new_project_will_be_created',
    'new_shift_type_will_be_created',
    'new_absence_type_will_be_created',
    'new_tag_will_be_created',
]);

export const getDataMappingKey = (field: string, sourceValue: string) => `${field}::${sourceValue}`;

export const normalizeImportValue = (value: string) => value.trim().toLowerCase();

const getNormalizedDataMappingKey = (field: string, sourceValue: string) =>
    getDataMappingKey(field, normalizeImportValue(sourceValue));

export const normalizeImportField = (value: string) =>
    value.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const isAbsenceRow = (row: TimeTrackingImportPreviewRowDto) => {
    const entityKind = String(row.entityKind ?? '').trim().toLowerCase();
    return row.entityKind === TimeTrackingImportEntityKind.Absence || entityKind === 'absence';
};

export const inferDataMappingTarget = (
    field: string,
    rows: TimeTrackingImportPreviewRowDto[]
): TimeTrackingImportDataMappingTarget => {
    const normalizedField = field.trim().toLowerCase();
    const hasOnlyAbsenceRows = rows.length > 0 && rows.every(isAbsenceRow);

    if (normalizedField.includes('employee')) {
        return 'employee';
    }

    if (normalizedField.includes('tag')) {
        return 'tag';
    }

    if (normalizedField.includes('type')) {
        return hasOnlyAbsenceRows ? 'absenceType' : 'shiftType';
    }

    if (normalizedField.includes('absence')) {
        return hasOnlyAbsenceRows ? 'absenceType' : 'project';
    }

    if (normalizedField.includes('project')) {
        return 'project';
    }

    return 'unknown';
};

const getRowValueByTarget = (
    row: TimeTrackingImportPreviewRowDto,
    target: TimeTrackingImportDataMappingTarget
) => {
    if (target === 'employee') {
        return row.employee;
    }

    if (target === 'tag') {
        return row.tags;
    }

    if (target === 'shiftType' || target === 'absenceType') {
        return row.type;
    }

    if (target === 'project') {
        return row.projectOrAbsence;
    }

    return '';
};

const shouldAddErrorCandidate = (
    field: string,
    value: string,
    rows: TimeTrackingImportPreviewRowDto[]
) => Boolean(value.trim() && inferDataMappingTarget(field, rows) !== 'unknown');

export const getDataMappingCandidates = (
    preview: TimeTrackingImportPreviewDto
): TimeTrackingImportDataMappingCandidate[] => {
    const candidatesByKey = new Map<string, {
        field: string;
        sourceValue: string;
        sourceValues: Set<string>;
        rowNumbers: Set<number>;
        defaultAction: TimeTrackingImportDataMappingAction;
    }>();

    const addCandidate = (
        field: string,
        sourceValue: string,
        rowNumbers: number[],
        defaultAction: TimeTrackingImportDataMappingAction
    ) => {
        const key = getNormalizedDataMappingKey(field, sourceValue);
        const existingCandidate = candidatesByKey.get(key);

        if (existingCandidate) {
            existingCandidate.sourceValues.add(sourceValue);
            rowNumbers.forEach((rowNumber) => existingCandidate.rowNumbers.add(rowNumber));
            return;
        }

        candidatesByKey.set(key, {
            field,
            sourceValue,
            sourceValues: new Set([sourceValue]),
            rowNumbers: new Set(rowNumbers),
            defaultAction,
        });
    };

    preview.unresolved.forEach((item) => {
        addCandidate(item.field, item.value, item.rowNumbers, 'create');
    });

    preview.rows.forEach((row) => {
        row.warnings
            .filter((warning) => NEW_ENTITY_WARNING_CODES.has(warning.code))
            .forEach((warning) => {
                addCandidate(warning.field, warning.value, [row.rowNumber], 'create');
            });

        row.errors
            .map((error) => {
                const target = inferDataMappingTarget(error.field, [row]);
                const value = error.value.trim() || getRowValueByTarget(row, target);

                return {
                    ...error,
                    value,
                };
            })
            .filter((error) => shouldAddErrorCandidate(error.field, error.value, [row]))
            .forEach((error) => {
                addCandidate(error.field, error.value, [row.rowNumber], 'create');
            });
    });

    return Array.from(candidatesByKey.values()).map((item) => {
        const rowNumbers = Array.from(item.rowNumbers).sort((left, right) => left - right);
        const rows = preview.rows.filter((row) => item.rowNumbers.has(row.rowNumber));
        const target = inferDataMappingTarget(item.field, rows);

        return {
            field: item.field,
            sourceValue: item.sourceValue,
            sourceValues: Array.from(item.sourceValues),
            rowNumbers,
            target,
            defaultAction: item.defaultAction,
        };
    });
};

export const createDefaultDataMappings = (
    preview: TimeTrackingImportPreviewDto
): TimeTrackingImportDataMappingDto[] => getDataMappingCandidates(preview).map((candidate) => ({
    field: candidate.field,
    sourceValue: candidate.sourceValue,
    target: candidate.target,
    action: candidate.defaultAction,
    targetId: null,
    targetName: candidate.defaultAction === 'create' ? candidate.sourceValue : '',
}));

export const isDataMappingComplete = (dataMapping?: TimeTrackingImportDataMappingDto) => {
    if (!dataMapping) {
        return false;
    }

    if (dataMapping.action === 'map') {
        return Boolean(dataMapping.targetId);
    }

    return Boolean(dataMapping.targetName?.trim());
};

export const dataMappingFieldMatches = (
    field: string,
    dataMapping: TimeTrackingImportDataMappingDto
) => {
    const normalizedField = normalizeImportField(field);
    const normalizedMappingField = normalizeImportField(dataMapping.field);

    if (
        normalizedField === normalizedMappingField
        || normalizedField.includes(normalizedMappingField)
        || normalizedMappingField.includes(normalizedField)
    ) {
        return true;
    }

    switch (dataMapping.target) {
        case 'employee':
            return normalizedField.includes('employee');
        case 'tag':
            return normalizedField.includes('tag');
        case 'shiftType':
        case 'absenceType':
            return normalizedField.includes('type');
        default:
            return normalizedField.includes('project')
                || normalizedField.includes('absence')
                || normalizedField.includes('projectorabsence');
    }
};

export const rowHasDataMappingValue = (
    row: TimeTrackingImportPreviewRowDto,
    dataMapping: TimeTrackingImportDataMappingDto
) => {
    const normalizedSource = normalizeImportValue(dataMapping.sourceValue);

    switch (dataMapping.target) {
        case 'employee':
            return normalizeImportValue(row.employee) === normalizedSource;
        case 'tag':
            return row.tags
                .split(',')
                .some((tag) => normalizeImportValue(tag) === normalizedSource);
        case 'shiftType':
        case 'absenceType':
            return normalizeImportValue(row.type) === normalizedSource;
        default:
            return normalizeImportValue(row.projectOrAbsence) === normalizedSource;
    }
};

export const importIssueValueMatchesDataMapping = (
    issue: TimeTrackingImportIssueDto,
    row: TimeTrackingImportPreviewRowDto,
    dataMapping: TimeTrackingImportDataMappingDto
) => {
    const normalizedIssueValue = normalizeImportValue(issue.value);
    const normalizedSourceValue = normalizeImportValue(dataMapping.sourceValue);
    const normalizedTargetName = normalizeImportValue(dataMapping.targetName ?? '');

    return normalizedIssueValue === normalizedSourceValue
        || Boolean(normalizedTargetName && normalizedIssueValue === normalizedTargetName)
        || rowHasDataMappingValue(row, dataMapping);
};

const findIssueDataMapping = (
    issue: TimeTrackingImportIssueDto,
    row: TimeTrackingImportPreviewRowDto,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => dataMappings.find((dataMapping) =>
    isDataMappingComplete(dataMapping)
    && importIssueValueMatchesDataMapping(issue, row, dataMapping)
    && dataMappingFieldMatches(issue.field, dataMapping)
);

// A mapping onto an existing record can never end with a new one being created. If the server
// still reports "will be created", the mapping did not reach it, and the message must not be
// hidden: otherwise the preview looks clean while the import creates a duplicate entity.
export const isUnappliedMapIssue = (
    issue: TimeTrackingImportIssueDto,
    row: TimeTrackingImportPreviewRowDto,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => NEW_ENTITY_WARNING_CODES.has(issue.code)
    && findIssueDataMapping(issue, row, dataMappings)?.action === 'map';

export const getIssueDataMapping = (
    issue: TimeTrackingImportIssueDto,
    row: TimeTrackingImportPreviewRowDto,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => isUnappliedMapIssue(issue, row, dataMappings)
    ? undefined
    : findIssueDataMapping(issue, row, dataMappings);

export const getDataMappingTargetLabel = (
    target: TimeTrackingImportDataMappingTarget,
    intl: IntlShape
) => {
    switch (target) {
        case 'project':
            return intl.formatMessage({id: Lx.General.PROJECT});
        case 'shiftType':
            return intl.formatMessage({id: Lx.General.SHIFT_TYPE});
        case 'absenceType':
            return intl.formatMessage({id: Lx.General.ABSENCE_TYPE});
        case 'tag':
            return intl.formatMessage({id: Lx.General.TAG_TYPE});
        case 'employee':
            return intl.formatMessage({id: Lx.General.EMPLOYEE});
        default:
            return intl.formatMessage({id: Lx.General.VALUE});
    }
};

export const getMappedImportValue = (
    value: string,
    dataMappings: TimeTrackingImportDataMappingDto[],
    targets?: TimeTrackingImportDataMappingDto['target'][]
) => {
    const normalizedValue = normalizeImportValue(value);
    const dataMapping = dataMappings.find((mappingItem) =>
        (!targets || targets.includes(mappingItem.target))
        && isDataMappingComplete(mappingItem)
        && (
            normalizeImportValue(mappingItem.sourceValue) === normalizedValue
            // The employee from the input goes to the server as an id reference (see
            // applyEmployeeDataMapping), so the preview returns "#12" — show the picked record name.
            || Boolean(getEmployeeIdReference(mappingItem))
            && normalizedValue === getEmployeeIdReference(mappingItem)
        )
    );

    return dataMapping?.targetName?.trim() || value;
};

const getEmployeeIdReference = (dataMapping: TimeTrackingImportDataMappingDto) =>
    dataMapping.target === 'employee' && dataMapping.action === 'map' && dataMapping.targetId
        ? `#${dataMapping.targetId}`
        : '';

export const getMappedImportTags = (
    value: string,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => {
    if (!value.trim()) {
        return value;
    }

    return value
        .split(',')
        .map((tag) => getMappedImportValue(tag, dataMappings, ['tag']))
        .join(', ');
};

export const getFieldDataMappings = (
    dataMappings: TimeTrackingImportDataMappingDto[],
    field: 'employee' | 'projectOrAbsence' | 'type' | 'tags'
) => dataMappings.filter((dataMapping) => {
    switch (field) {
        case 'employee':
            return dataMapping.target === 'employee';
        case 'tags':
            return dataMapping.target === 'tag';
        case 'type':
            return dataMapping.target === 'shiftType' || dataMapping.target === 'absenceType';
        default:
            return dataMapping.target === 'project';
    }
});

export const getMappedImportProjectOrAbsence = (
    row: TimeTrackingImportPreviewRowDto,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => {
    const mappedProject = getMappedImportValue(row.projectOrAbsence, dataMappings, ['project']);

    if (mappedProject !== row.projectOrAbsence) {
        return mappedProject;
    }

    return getMappedImportValue(row.projectOrAbsence, dataMappings, ['absenceType']);
};

export const getMappedImportType = (
    row: TimeTrackingImportPreviewRowDto,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => getMappedImportValue(row.type, dataMappings, ['shiftType', 'absenceType']);

export const getCreateWarningCode = (dataMapping: TimeTrackingImportDataMappingDto) =>
    `mapped_${dataMapping.target}_will_be_created`;

export const getEffectiveIssues = (
    row: TimeTrackingImportPreviewRowDto,
    dataMappings: TimeTrackingImportDataMappingDto[],
    intl: IntlShape
) => {
    const effectiveErrors = row.errors.filter((issue) => !getIssueDataMapping(issue, row, dataMappings));
    const effectiveWarnings = row.warnings.filter((issue) => !getIssueDataMapping(issue, row, dataMappings));
    const unappliedMapErrors = effectiveWarnings
        .filter((issue) => isUnappliedMapIssue(issue, row, dataMappings))
        .map((issue): TimeTrackingImportIssueDto => {
            const dataMapping = findIssueDataMapping(issue, row, dataMappings);

            return {
                ...issue,
                message: intl.formatMessage(
                    {id: Lx.TimeTrackingImport.MAPPING_NOT_APPLIED},
                    {
                        target: dataMapping
                            ? getDataMappingTargetLabel(dataMapping.target, intl).toLowerCase()
                            : issue.field,
                        name: dataMapping?.targetName?.trim() || issue.value,
                    }
                ),
            };
        });
    const createWarnings = dataMappings
        .filter((dataMapping) =>
            dataMapping.action === 'create'
            && isDataMappingComplete(dataMapping)
            && rowHasDataMappingValue(row, dataMapping)
        )
        .map((dataMapping): TimeTrackingImportIssueDto => ({
            code: getCreateWarningCode(dataMapping),
            field: dataMapping.field,
            value: dataMapping.targetName?.trim() || dataMapping.sourceValue,
            message: intl.formatMessage(
                {id: Lx.TimeTrackingImport.MAPPED_TARGET_WILL_BE_CREATED},
                {
                    target: getDataMappingTargetLabel(dataMapping.target, intl),
                    name: dataMapping.targetName?.trim() || dataMapping.sourceValue,
                }
            ),
        }));

    return {
        errors: [...effectiveErrors, ...unappliedMapErrors],
        warnings: [
            ...effectiveWarnings.filter((issue) => !isUnappliedMapIssue(issue, row, dataMappings)),
            ...createWarnings,
        ],
    };
};

export const getEffectiveStatus = (
    errors: TimeTrackingImportIssueDto[],
    warnings: TimeTrackingImportIssueDto[]
) => {
    switch (true) {
        case errors.length > 0:
            return TimeTrackingImportRowStatus.Error;
        case warnings.length > 0:
            return TimeTrackingImportRowStatus.Warning;
        default:
            return TimeTrackingImportRowStatus.Ready;
    }
};

const PENDING_ENTITY_WARNING_CODES: Record<string, TimeTrackingImportDataMappingTarget> = {
    new_project_will_be_created: 'project',
    new_shift_type_will_be_created: 'shiftType',
    new_absence_type_will_be_created: 'absenceType',
    new_tag_will_be_created: 'tag',
};

/**
 * Names of the entities that do not exist yet but will appear after this import: from the mapping
 * step (the "create" action) and from the preview warnings. Manual row selectors need them —
 * otherwise a project that is only being added cannot be referenced.
 */
export const getPendingEntityNames = (
    preview: TimeTrackingImportPreviewDto,
    dataMappings: TimeTrackingImportDataMappingDto[],
    target: TimeTrackingImportDataMappingTarget
): string[] => {
    const names = dataMappings
        .filter((dataMapping) =>
            dataMapping.target === target
            && dataMapping.action === 'create'
            && isDataMappingComplete(dataMapping))
        .map((dataMapping) => dataMapping.targetName?.trim() ?? '');

    preview.rows.forEach((row) => {
        row.warnings.forEach((warning) => {
            if (PENDING_ENTITY_WARNING_CODES[warning.code] !== target) {
                return;
            }

            names.push(warning.value.trim());
        });
    });

    return names.filter(Boolean);
};

export const getRemainingUnresolvedItems = (
    preview: TimeTrackingImportPreviewDto,
    dataMappings: TimeTrackingImportDataMappingDto[]
) => preview.unresolved.filter((item) => {
    const dataMapping = dataMappings.find((mappingItem) =>
        normalizeImportValue(mappingItem.sourceValue) === normalizeImportValue(item.value)
        && dataMappingFieldMatches(item.field, mappingItem)
    );

    return !isDataMappingComplete(dataMapping);
});

// When the file has no employee column, the name is typed in the input and never reaches the file
// — so buildMappedImportFile cannot apply the mapping to it, and the original misspelled value
// goes to the server. That is why the employee mapping is applied to employeeName itself.
export const applyEmployeeDataMapping = (
    columnMapping: TimeTrackingImportColumnMapping,
    dataMappings: TimeTrackingImportDataMappingDto[]
): TimeTrackingImportColumnMapping => {
    const employeeName = columnMapping.employeeName.trim();

    if (columnMapping.employeeColumn.trim() || !employeeName) {
        return columnMapping;
    }

    const dataMapping = getFieldDataMappings(dataMappings, 'employee').find(
        (mappingItem) =>
            isDataMappingComplete(mappingItem)
            && normalizeImportValue(mappingItem.sourceValue) === normalizeImportValue(employeeName)
    );

    if (!dataMapping) {
        return columnMapping;
    }

    if (dataMapping.action === 'map') {
        // An id reference instead of a name: ResolveEmployee understands "#12", and it does not depend
        // on how the server stores the name or on employees sharing one.
        return dataMapping.targetId
            ? {...columnMapping, employeeName: `#${dataMapping.targetId}`}
            : columnMapping;
    }

    return {
        ...columnMapping,
        employeeName: dataMapping.targetName?.trim() || columnMapping.employeeName,
    };
};

const csvEscape = (value: string) => {
    const normalizedValue = value ?? '';

    if (/[",\r\n]/.test(normalizedValue)) {
        return `"${normalizedValue.replace(/"/g, '""')}"`;
    }

    return normalizedValue;
};

export const buildMappedImportFile = (
    sourceFile: File,
    preview: TimeTrackingImportPreviewDto,
    dataMappings: TimeTrackingImportDataMappingDto[],
    columnMapping: TimeTrackingImportColumnMapping,
    manualRows: ManualImportRow[] = [],
    fileRowChanges: ImportFileRowChanges = EMPTY_FILE_ROW_CHANGES
) => {
    const entityKindColumn = getImportEntityKindColumn(manualRows, fileRowChanges);
    const overtimeColumn = getImportOvertimeColumn(manualRows, fileRowChanges);
    const headers = [
        columnMapping.employeeColumn,
        columnMapping.dateColumn,
        columnMapping.hoursColumn,
        columnMapping.projectOrAbsenceColumn,
        columnMapping.typeColumn,
        columnMapping.noteColumn,
        columnMapping.tagsColumn,
        entityKindColumn,
        overtimeColumn,
    ].filter((header, index, list) => header.trim() && list.indexOf(header) === index);
    const getRowValue = (
        row: TimeTrackingImportPreviewRowDto,
        header: string
    ) => {
        switch (header) {
            case columnMapping.employeeColumn:
                return getMappedImportValue(row.employee, dataMappings, ['employee']);
            case columnMapping.dateColumn:
                return row.date;
            case columnMapping.hoursColumn:
                return row.hours;
            case columnMapping.projectOrAbsenceColumn:
                return getMappedImportProjectOrAbsence(row, dataMappings);
            case columnMapping.typeColumn:
                return getMappedImportType(row, dataMappings);
            case columnMapping.noteColumn:
                return row.note;
            case columnMapping.tagsColumn:
                return getMappedImportTags(row.tags, dataMappings);
            default:
                return '';
        }
    };
    const buildManualRowLine = (manualRow: ManualImportRow) => headers
        .map((header) => csvEscape(getManualImportRowValue(manualRow, header, columnMapping)))
        .join(',');
    const csvRows = [
        headers.map(csvEscape).join(','),
        // Removed file rows simply do not reach the file, rewritten ones go instead of the originals —
        // the order of the file rows is preserved (see buildImportRowTargets).
        ...preview.rows
            .map((row, index) => {
                if (fileRowChanges.excludedIndexes.includes(index)) {
                    return null;
                }

                const override = fileRowChanges.overrides[index];

                return override
                    ? buildManualRowLine({...override, id: `file-${index}`})
                    : headers.map((header) => csvEscape(getRowValue(row, header))).join(',');
            })
            .filter((line): line is string => line !== null),
        // Manual rows are always appended after the file rows and in the same order as in the page state:
        // preview rows are later matched to the manual records by that order.
        ...manualRows.map(buildManualRowLine),
    ];
    const fileName = sourceFile.name.replace(/\.[^.]+$/, '') || 'time-tracking-import';

    return new File([csvRows.join('\r\n')], `${fileName}-mapped.csv`, {type: 'text/csv'});
};
